import { useEffect } from "react";
import { v4 } from "uuid";
import { toast } from "sonner";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import {
  PatchPositionRequest,
  updateHtzOfferCache,
  useHtzOfferAddPositionMutation,
  useHtzOfferPatchPositionMutation,
  useHtzOfferRemovePositionMutation,
  useHtzOfferShowQuery,
} from "@/services/backend/htz/offers/api/offer-api";
import {
  patchPosition,
  Position,
  positionPatchFromCatalog,
} from "@/services/backend/htz/position/types/position";
import { usePositionsTableDependencies } from "@/services/backend/htz/position/hooks/use-positions-table-dependencies";
import {
  PositionsTable,
  PositionsTableSkeleton,
} from "@/services/backend/htz/position/components/positions-table";
import { PositionRow } from "@/services/backend/htz/position/components/position-row";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import {
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { parseRTKQueryErrorToSonner } from "@/shared/components/errors/parse-r-t-k-query-error";

/**
 * OfferPositionsTable edits the positions of the offer. Every change of a
 * position is saved on its own.
 */
export function OfferPositionsTable({ offerId }: { offerId: string }) {
  const { data: offer, error } = useHtzOfferShowQuery({ id: offerId });
  const {
    catalog,
    airHandlingUnits,
    isLoading,
    error: dependenciesError,
  } = usePositionsTableDependencies(offer?.companyId, offer?.customerId);

  if (error || dependenciesError) {
    return <RTKQueryErrorAlert error={error ?? dependenciesError} />;
  }

  if (!offer || !catalog || isLoading) {
    return <PositionsTableSkeleton />;
  }

  return (
    <Positions
      offer={offer}
      catalog={catalog}
      airHandlingUnits={airHandlingUnits}
    />
  );
}

function Positions({
  offer,
  catalog,
  airHandlingUnits,
}: {
  offer: Offer;
  catalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
}) {
  const [add, { error, reset }] = useHtzOfferAddPositionMutation();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  const disabled = isImmutable(offer);

  return (
    <PositionsTable
      positions={offer.positions}
      catalog={catalog}
      onAdd={(item) =>
        add({
          id: offer.id,
          positionId: v4(),
          ...positionPatchFromCatalog(item),
        })
      }
      disabled={disabled}
    >
      {offer.positions.map((position) => (
        <OfferPositionRow
          key={position.id}
          offer={offer}
          position={position}
          airHandlingUnits={airHandlingUnits}
          disabled={disabled}
        />
      ))}
    </PositionsTable>
  );
}

function OfferPositionRow({
  offer,
  position,
  airHandlingUnits,
  disabled,
}: {
  offer: Offer;
  position: Position;
  airHandlingUnits: AirHandlingUnit[];
  disabled: boolean;
}) {
  const request: PatchPositionRequest = {
    id: offer.id,
    positionId: position.id,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzOfferCache,
    useHtzOfferPatchPositionMutation(),
  );
  const [remove, { error, reset }] = useHtzOfferRemovePositionMutation();
  const { resetFields } = useErrValidationFailedContext();
  useEffect(() => {
    if (error) {
      toast(...parseRTKQueryErrorToSonner(error));
      reset();
    }
  }, [error, reset]);

  return (
    <PositionRow
      position={position}
      onPatch={(patch) =>
        set(
          { ...request, ...patch },
          {
            ...offer,
            positions: patchPosition(offer.positions, position.id, patch),
          },
        )
      }
      onRemove={() =>
        remove(request)
          .unwrap()
          .then(() => resetFields(joinFieldName("positions", position.id)))
          .catch(() => {
            // surfaced through the error state above
          })
      }
      airHandlingUnits={airHandlingUnits}
      disabled={disabled}
    />
  );
}
